import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import prune from 'underscore.string/prune';
import Event from '@fuww/library/src/Layouts/EventPage';
import { Breadcrumbs } from '@fuww/library/src/Breadcrumb';
import Button from '@fuww/library/src/Button';
import { FormattedDate, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import Head from '../Head';
import withQuery from '../../lib/withQuery';
import throw404 from '../../lib/throw404';
import getEventJsonLdData from '../../lib/getEventJsonLdData';
import messages from '../../lib/messages';
import HomeLink from '../HomeLink';
import LinkAndAnchor from '../LinkAndAnchor';
import { useSiteContext } from '../SiteContext';
import Image from '../Image';
import getGraphCmsImageUrl from '../../lib/getGraphCmsImageUrl';
import { DEFAULT_EVENT_IMAGES } from '../../lib/constants';
import { getRouterState } from '../../lib/savedRouterState';
import getProxiedImages from '../../lib/getProxiedImages';

const PLACEHOLDER_URL = 'https://media.graphcms.com/resize=w:42/';

const variables = {
  imageConfigurations: [
    {
      width: 42,
      height: 28,
      quality: 10,
      resize: 'FILL',
    },
    {
      width: 375, height: 250, quality: 70, resize: 'FILL',
    },
    {
      width: 414, height: 276, quality: 70, resize: 'FILL',
    },
    {
      width: 720, height: 480, quality: 70, resize: 'FILL',
    },
  ],
};

const {
  imageUrls: fallbackImageUrls,
  srcSet: fallbackSourceSet,
} = getProxiedImages(variables, DEFAULT_EVENT_IMAGES);

const EventDetails = ({
  data: { event },
}) => {
  const intl = useIntl();
  const { url } = useSiteContext();
  const router = useRouter();
  const [lastEventsQuery, setLastEventsQuery] = useState(null);

  useEffect(() => {
    setLastEventsQuery({ ...getRouterState('events')?.query });
  }, []);

  if (!event) return throw404();

  const {
    id,
    title,
    startDate,
    endDate,
    content,
    streetAddress,
    addressLocality,
    addressCountry,
    coverImage,
    website,
    slug,
  } = event;
  const location = `${streetAddress || ''} ${addressLocality},
${addressCountry}`;

  const eventImage = coverImage
    ? getGraphCmsImageUrl(coverImage)
    : DEFAULT_EVENT_IMAGES[3];

  const placeholder = coverImage
    ? (`${PLACEHOLDER_URL}${coverImage.handle}`)
    : fallbackImageUrls[0];

  const sourceSet = coverImage
    ? `${getGraphCmsImageUrl(coverImage, 375)} 375w,
    ${getGraphCmsImageUrl(coverImage, 414)} 414w,
    ${getGraphCmsImageUrl(coverImage, 720)} 720w`
    : fallbackSourceSet;

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor
      route="events"
      passHref
    >
      {intl.formatMessage(messages.events)}
    </LinkAndAnchor>,
    <LinkAndAnchor
      route="event"
      passHref
      params={{ slug }}
    >
      {title}
    </LinkAndAnchor>,
  ];

  return (
    <>
      <Head
        title={title}
        description={prune(content, 160)
          || intl.formatMessage(messages.eventsMetaDescription)}
        jsonLdData={getEventJsonLdData(intl, event, url, eventImage)}
        subjectType="Event"
        subjectId={id}
      />
      <Event
        breadcrumbs={(
          <Breadcrumbs
            crumbs={breadcrumbs}
          />
        )}
        title={title}
        content={<ReactMarkdown>{content}</ReactMarkdown>}
        website={website}
        location={location}
        image={(
          <Image
            src={eventImage}
            srcSet={sourceSet}
            placeholder={placeholder}
            alt={title}
            title={title}
            width="100%"
            height="240px"
            sizes="(min-width: 768px) 720px, 100vw"
            finalFetchpriority="high"
            placeholderFetchpriority="high"
            preload
          />
        )}
        formattedStartDate={(
          <FormattedDate
            value={startDate}
            year="numeric"
            month="long"
            day="numeric"
          />
        )}
        formattedEndDate={(
          <FormattedDate
            value={endDate}
            year="numeric"
            month="long"
            day="numeric"
          />
        )}
        backButton={(
          <LinkAndAnchor
            route="events"
            passHref
            params={lastEventsQuery}
            onClick={(event_) => {
              if (lastEventsQuery) {
                event_.preventDefault();

                router.back();
              }
            }}
            scroll={!lastEventsQuery}
            useRouter
          >
            <Button
              variant="text"
              className="event-detail-backbutton"
            >
              EVENT CALENDAR
            </Button>
          </LinkAndAnchor>
        )}
      />
    </>
  );
};

EventDetails.propTypes = {
  data: PropTypes.shape({
    event: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      content: PropTypes.string,
      streetAddress: PropTypes.string,
      addressLocality: PropTypes.string,
      addressCountry: PropTypes.string,
      coverImage: PropTypes.shape({ handle: PropTypes.string }),
      website: PropTypes.string,
      slug: PropTypes.string,
    }),
  }).isRequired,
};

export default withQuery(EventDetails);
